import { h } from "../dom.js";
import { focusAfterWrite } from "./components.js";

/** Relative under 60 minutes; absolute clock time after (docs §5). */
export function formatFreshness(updatedAt: Date, now = new Date()): string {
  const diffMs = Math.max(0, now.getTime() - updatedAt.getTime());
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) {
    return `Updated ${String(Math.max(0, minutes))}m ago`;
  }
  return `Updated ${updatedAt.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

/**
 * rd R8-11: a Refresh repaints the page, which replaces this caption and the
 * button that was pressed, so focus fell to the page body. The next caption
 * this module builds takes the focus back. Bounded in time: a Refresh whose
 * page never repaints (a failure that leaves the old page up) must not move
 * focus later, on some unrelated page.
 */
const REFRESH_FOCUS_WINDOW_MS = 5_000;
let pendingRefreshFocusUntil = 0;

export interface FreshnessCaptionOptions {
  updatedAt: Date;
  onRefresh: () => void;
}

/** Quiet caption: `Updated 2m ago · Refresh` */
export function createFreshnessCaption(opts: FreshnessCaptionOptions): HTMLElement {
  const refresh = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "refresh",
      "aria-label": "Refresh",
    },
    ["Refresh"],
  );
  refresh.addEventListener("click", () => {
    pendingRefreshFocusUntil = Date.now() + REFRESH_FOCUS_WINDOW_MS;
    opts.onRefresh();
  });
  const caption = h("p", { class: "freshness", "data-role": "freshness" }, [
    formatFreshness(opts.updatedAt),
    document.createTextNode(" \u00b7 "),
    refresh,
  ]);
  if (pendingRefreshFocusUntil > 0 && Date.now() < pendingRefreshFocusUntil) {
    pendingRefreshFocusUntil = 0;
    // After the caller has put the caption on the page.
    queueMicrotask(() => {
      focusAfterWrite(refresh);
    });
  }
  return caption;
}
